import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useGetPresignedUrlQuery, useLazyGetFilesSimpleSearchQuery } from '../../api/file.js';
import BaseAutocomplete from '../form/BaseAutocomplete.jsx';
import BaseButton from '../form/BaseButton.jsx';
import BaseCard from './BaseCard.jsx';
import BaseUpload from '../form/BaseUpload.jsx';
import { getFileExtension, getFileNameWithoutExtension } from '../../utils/file.js';
import IconFiles from '../images/IconFiles.jsx';
import { PERMISSION_FILES } from '../../utils/constants.js';
import useCurrentUser from '../../hooks/useCurrentUser.js';

const AssociateFiles = ({ fields, append, remove, showSearch, setShowSearch }) => {
    const [search, setSearch] = useState('');
    const [fileList, setFileList] = useState([]);
    const [s3Error, setS3Error] = useState('');

    const { permissions: userPermissions } = useCurrentUser();

    const debouncedSearch = useDebounce(search, 500);

    const [triggerSimpleSearch, { isLoading: isLoadingFiles }] = useLazyGetFilesSimpleSearchQuery();

    const { data: presignedData, isLoading: isLoadingPresigned } = userPermissions[PERMISSION_FILES].create
        ? useGetPresignedUrlQuery()
        : { data: [], isLoading: false };
    const [presignedUrl, setPresignedUrl] = useState('');
    const [presignedFields, setPresignedFields] = useState({});

    const extensions = {
        bmp: 'media',
        gif: 'media',
        png: 'media',
        jpg: 'media',
        jpeg: 'media',
        webp: 'media',
        svg: 'media',
        tif: 'media',
        tiff: 'media',
        heic: 'media',
        heif: 'media',
        mp3: 'media',
        wav: 'media',
        mp4: 'media',
        avi: 'media',
        pdf: 'document',
        doc: 'document',
        docx: 'document',
        txt: 'document',
        xls: 'spreadsheet',
        xlsx: 'spreadsheet',
        csv: 'spreadsheet'
    };

    /**
     * A small map from extension -> 'type', same as in ImportFiles
     */
    const mapExtensionToType = (extension) => {
        return extensions[extension];
    };

    useEffect(() => {
        if (presignedData?.response) {
            setPresignedUrl(presignedData.response.url);
            setPresignedFields(presignedData.response.fields);
        }
    }, [presignedData]);

    useEffect(() => {
        (async () => {
            if (debouncedSearch.trim() !== '') {
                const result = await triggerSimpleSearch({ search: debouncedSearch });
                if (result?.data?.response?.files) {
                    setFileList(result.data.response.files);
                } else {
                    setFileList([]);
                }
            } else {
                setFileList([]);
            }
        })();
    }, [debouncedSearch, triggerSimpleSearch]);

    /**
     * Called when selecting a file from the search results
     * We store it as a reference to an existing file by setting file_id but leaving the other fields blank
     */
    const selectFile = (fileId) => {
        const selectedFile = fileList.find((f) => f.id === fileId);

        if (selectedFile) {
            append({
                file_id: selectedFile.id,
                name: selectedFile.title || '',
                path: selectedFile.url || '',
                type: '',
                extension: ''
            });
        }
        setSearch('');
    };

    /**
     * Resets the search box and re-shows the search area
     */
    const resetSearch = () => {
        setShowSearch(true);
        setFileList([]);
        setSearch('');
    };

    /**
     * Generate a unique file name for S3 to avoid collisions
     */
    const generateUniqueFileName = (originalName) => {
        const timestamp = Date.now();
        const randomString = Math.random().toString(36).substring(2, 8);
        const extension = originalName.split('.').pop();
        return `${timestamp}_${randomString}.${extension}`;
    };

    /**
     * Upload a single file to S3 using the presigned URL
     * Returns an object with the needed fields if successful
     */
    const uploadFileToS3 = async (file) => {
        try {
            if (!presignedUrl || !presignedFields.key) {
                throw new Error('Missing presigned URL or S3 fields.');
            }

            const uniqueFilename = generateUniqueFileName(file.name);
            const fileKey = `${presignedFields.key}${uniqueFilename}`;
            const formData = new FormData();

            Object.entries(presignedFields).forEach(([key, value]) => {
                formData.append(key, value);
            });

            formData.set('key', fileKey);

            formData.append('file', file);

            const response = await axios.post(presignedUrl, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.status !== 204) {
                throw new Error(`Failed to upload file: ${response.statusText}`);
            }

            const extension = getFileExtension(file.name);
            return {
                file_id: '',
                path: fileKey,
                name: getFileNameWithoutExtension(file.name),
                extension,
                type: mapExtensionToType(extension)
            };
        } catch (error) {
            console.error(error);
            if (error.response) {
                if (error.response.status === 403 || error.response.status === 401) {
                    setS3Error('Invalid credentials provided for S3 upload.');
                } else {
                    setS3Error(`S3 upload failed: ${error.response.statusText}`);
                }
            } else if (error.request) {
                setS3Error('No response received from S3. Please check your network.');
            } else {
                setS3Error('Unexpected error during file upload.');
            }
            return null;
        }
    };

    /**
     * Called by BaseUpload when user selects new files to upload
     * We'll upload each file to S3 immediately, then append it into the form
     */
    const onFilesUpload = async (acceptedFiles) => {
        setS3Error('');

        for (const file of acceptedFiles) {
            const uploaded = await uploadFileToS3(file);
            if (uploaded) {
                append(uploaded);
            }
        }
    };

    return (
        <BaseCard className='border-0 bg-theme1-primary2 p-0 pb-6 mt-0 max-w-3xl w-full mx-auto'>
            {/* Header */}
            <div className='max-w-3xl w-full mx-auto px-8 flex items-center justify-between p-6'>
                <div className='text-2xl font-bold text-theme1-white'>Files</div>
                {/* <div onClick={resetSearch}>
                    <IconAdd className='w-8 h-8 fill-theme1-white' />
                </div> */}
            </div>

            <BaseCard className='border-0 m-0 mb-6 p-4 pb-5 bg-theme1-gray'>
                <div className='max-w-3xl w-full mx-auto px-4'>
                    <div className='text-lg font-bold text-theme1-title2 pb-2'>Associate Files</div>

                    <div className='flex flex-col space-y-4'>
                        {showSearch && (
                            <div className='flex justify-between items-center'>
                                <div className='flex-grow'>
                                    <BaseAutocomplete
                                        items={fileList.map((file) => ({
                                            label: file.title,
                                            value: file.id
                                        }))}
                                        onSelect={selectFile}
                                        name='searchFiles'
                                        value={search}
                                        placeholder='Search...'
                                        setValue={(val) => setSearch(val)}
                                        isLoading={isLoadingFiles}
                                        exclude={fields.filter((item) => !!item.file_id).map((item) => item.file_id)}
                                    />
                                </div>

                                <div className='text-theme1-primary2 cursor-pointer ml-4' onClick={resetSearch}>
                                    Reset
                                </div>
                            </div>
                        )}

                        {!!userPermissions[PERMISSION_FILES].create && (
                            <BaseUpload
                                accept={Object.keys(extensions)}
                                onFilesUpload={onFilesUpload}
                                multiple={true}
                                isLoading={isLoadingPresigned}
                                errors={s3Error ? [s3Error] : []}
                                canDelete={false}
                            />
                        )}
                    </div>

                    {fields.length > 0 && (
                        <>
                            <hr className='my-4 border-theme1-disabled' />
                            {fields.map((file, index) => {
                                return (
                                    <div
                                        key={`${file.file_id || 'new'}-${file.path}-${index}`}
                                        className='w-full flex items-center justify-between mt-4 bg-theme1-white rounded-lg p-4 border-theme1-cardStroke'
                                    >
                                        <div className='flex flex-row items-center justify-center overflow-hidden'>
                                            <div className='mr-4'>
                                                <div className='rounded-full w-12 h-12 overflow-hidden'>
                                                    {file.path.match(
                                                        /\.(jpg|jpeg|png|gif|webp|svg|tif|tiff|bmp|heic|heif)$/
                                                    ) ? (
                                                        <img
                                                            src={
                                                                file.file_id
                                                                    ? file.path
                                                                    : `${presignedUrl}/${file.path}`
                                                            }
                                                            alt={file.name}
                                                            className='h-full w-full object-cover rounded-t-2xl'
                                                        />
                                                    ) : (
                                                        <div className='w-full h-full bg-theme1-gold flex items-center justify-center'>
                                                            <IconFiles className='w-7 h-7 stroke-theme1-white' />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <div className='text-theme1-primary font-bold text-lg [word-break:break-word]'>
                                                    {file.name}
                                                </div>
                                            </div>
                                        </div>
                                        <BaseButton
                                            className='ml-2 btn-red max-w-32 p-2'
                                            onClick={() => remove(index)}
                                            textValue='Remove'
                                        />
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
            </BaseCard>
        </BaseCard>
    );
};

export default AssociateFiles;
